/**
 * PII redaction for outbound error reports — docs/spec/06 §6, 08 §4.
 *
 * This system routes mail to incarcerated people. An error payload that
 * casually carries an inmate's name, number, or address has exported exactly
 * the data we are most obliged to protect, to a third-party service, for the
 * sake of a stack trace.
 *
 * So redaction is a **denylist of shapes AND an allowlist of intent**: keys
 * that look sensitive are replaced wholesale, values that look like
 * credentials or scans are replaced regardless of key, and anything
 * unrecognised but long is truncated rather than shipped in full.
 */

export const REDACTED = '[redacted]'

/**
 * Keys whose values never leave the browser.
 *
 * Deliberately broad: `name` catches sender and inmate names alike, and the
 * cost of over-redacting a debugging field is far lower than the cost of
 * exporting one real person's address.
 */
const SENSITIVE_KEY = new RegExp(
  [
    'password',
    'token',
    'authorization',
    'secret',
    'api[-_]?key',
    'cookie',
    // People
    'name',
    'inmate',
    'sender',
    'recipient',
    'did',
    'phone',
    'email',
    // Places
    'address',
    'line_?one',
    'line_?two',
    'city',
    'district',
    'postal',
    'zip',
    'facility',
    'housing',
    // Mail content
    'scan',
    'image',
    'public_url',
    'content',
    'letter',
  ].join('|'),
  'i',
)

/** A data URI or a JWT, wherever it appears. */
const SENSITIVE_VALUE = /^data:|^ey[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\./

const MAX_STRING = 200
const MAX_DEPTH = 6

function redactString(value: string): string {
  if (SENSITIVE_VALUE.test(value)) return REDACTED
  return value.length > MAX_STRING ? `${value.slice(0, MAX_STRING)}…[truncated]` : value
}

/**
 * Deep-copy a value with sensitive content removed.
 *
 * Never throws: a redaction failure must not become the error you actually
 * report, and must certainly not fall back to sending the raw object.
 */
export function redact(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) return REDACTED
  if (value === null || value === undefined) return value

  if (typeof value === 'string') return redactString(value)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (typeof value === 'bigint') return String(value)
  if (typeof value === 'function' || typeof value === 'symbol') return REDACTED

  if (Array.isArray(value)) return value.map((item) => redact(item, depth + 1))

  if (value instanceof Error) {
    return { name: value.name, message: redactString(value.message) }
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = SENSITIVE_KEY.test(key) ? REDACTED : redact(item, depth + 1)
    }
    return out
  }

  return REDACTED
}

/** True when a payload still contains something that must not be sent. */
export function containsSensitiveKey(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false
  if (Array.isArray(value)) return value.some(containsSensitiveKey)
  return Object.entries(value as Record<string, unknown>).some(
    ([key, item]) => SENSITIVE_KEY.test(key) || containsSensitiveKey(item),
  )
}
